import type { IntegrationType } from "@v2/features/settings/shop/integrations/IntegrationForm";
import type { ToolCallMessageContent } from "schemas/chat";

import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { IntegrationDetailContent } from "@v2/features/integrations/IntegrationDetailPage";
import { INTEGRATION_CONFIGS } from "@v2/features/settings/shop/integrations/config";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";

type IntegrationConnectCardProps = {
  toolCall: ToolCallMessageContent;
  isConfigured: boolean;
};

const INTEGRATION_TYPE_TO_CONFIG_KEY: Record<
  string,
  keyof typeof INTEGRATION_CONFIGS | undefined
> = {
  Klaviyo: "Klaviyo",
  GA4: "GA4",
  GoogleTagManager: "GoogleTagManager",
  Hotjar: "Hotjar",
  MicrosoftClarity: "MicrosoftClarity",
  Snapchat: "Snapchat",
  TikTok: "TikTok",
  Pinterest: "Pinterest",
  Reddit: "Reddit",
  Meta: "Meta",
  Triplewhale: "Triplewhale",
};

export default function IntegrationConnectCard({
  toolCall,
  isConfigured,
}: IntegrationConnectCardProps) {
  const projectId = useCurrentProjectId();
  const cardRef = React.useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = React.useState({ x: 50, y: 50 });
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const rawIntegrationType = toolCall.input?.integrationType as
    | string
    | undefined;
  const configKey = rawIntegrationType
    ? INTEGRATION_TYPE_TO_CONFIG_KEY[rawIntegrationType]
    : undefined;
  const config = configKey ? INTEGRATION_CONFIGS[configKey] : undefined;

  if (!config || !rawIntegrationType || !projectId) {
    return null;
  }

  const integrationType = configKey as IntegrationType;

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleEnable = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) {
      return;
    }
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePosition({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePosition({ x: 50, y: 50 });
  };

  // Calculate gradient angle based on mouse position
  const angle =
    Math.atan2(mousePosition.y - 50, mousePosition.x - 50) * (180 / Math.PI);

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="group relative flex w-full flex-col gap-4 rounded-xl border-0.5 border-border overflow-hidden bg-white p-5 text-left transition-all hover:shadow-sm"
    >
      {/* Gradient background - top half only, with interactive angle */}
      <div
        className="absolute top-0 left-0 right-0 h-24 opacity-15 blur-xl transition-all duration-300 ease-out"
        style={{
          background: `linear-gradient(${angle + 90}deg, #B7FEAE 0%, #FFD873 33%, #FE816E 66%, #DA6AF3 100%)`,
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col gap-2 w-full">
        {/* Logo and button on same line */}
        <div className="flex items-center justify-between gap-3">
          <img
            src={config.image}
            alt={config.title}
            className="size-8 rounded-lg object-contain shrink-0"
          />
          <Button
            variant="secondary"
            size="base"
            onClick={isConfigured ? handleEdit : handleEnable}
          >
            {isConfigured ? "Edit" : "Enable"}
          </Button>
        </div>

        {/* Title and tag */}
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold text-default">
            {config.title}
          </span>
          {isConfigured && (
            <Tag variant="success" size="sm">
              Connected
            </Tag>
          )}
        </div>

        <span className="text-sm text-muted">{config.description}</span>
      </div>

      {/* Integration Modal */}
      <Modal
        open={isModalOpen}
        onOpenChange={(open) => setIsModalOpen(open)}
        size="lg"
        showCloseButton={false}
        dismissable={false}
        UNSAFE_className="max-h-[90vh] overflow-y-auto"
      >
        <IntegrationDetailContent
          integrationType={integrationType}
          projectId={projectId}
          onBack={() => setIsModalOpen(false)}
          inModal
        />
      </Modal>
    </div>
  );
}
